import type { ReactNode } from "react";
import { Fleuron, TapeCorner } from "../../../components/design";

export type ArticleSection = {
  h2: string;
  body: ReactNode;
  bullets?: ReactNode[];
  quote?: { text: string; who: string };
};

type ArticleProps = {
  sections: ArticleSection[];
};

// Renders the H2 sections of a marketing landing page. Each section can carry
// a body, an optional bullet list, and an optional pull-quote rendered as a
// taped paper card.
export default function Article({ sections }: ArticleProps) {
  return (
    <article>
      {sections.map((s, i) => (
        <section key={i} style={{ padding: "24px 0 8px" }}>
          <h2
            style={{
              fontFamily: "var(--f-display)",
              fontWeight: 400,
              fontSize: 26,
              lineHeight: 1.12,
              letterSpacing: "-0.012em",
              margin: "0 0 14px",
              color: "var(--ink)",
            }}
          >
            {s.h2}
          </h2>
          <div
            style={{
              fontFamily: "var(--f-body)",
              fontSize: 16.5,
              lineHeight: 1.55,
              color: "var(--ink-soft)",
            }}
          >
            {s.body}
          </div>

          {s.bullets && s.bullets.length > 0 && (
            <ul
              style={{
                margin: "12px 0 0",
                paddingLeft: 22,
                fontFamily: "var(--f-body)",
                fontSize: 16,
                lineHeight: 1.55,
                color: "var(--ink-soft)",
              }}
            >
              {s.bullets.map((b, j) => (
                <li key={j} style={{ marginBottom: 6 }}>
                  {b}
                </li>
              ))}
            </ul>
          )}

          {s.quote && (
            <div
              style={{
                position: "relative",
                margin: "20px 0 4px",
                background: "var(--paper-50)",
                border: "1px solid rgba(43,26,15,0.10)",
                borderRadius: 4,
                padding: "20px 20px 16px",
                boxShadow:
                  "0 1px 0 rgba(43,26,15,0.04), 0 10px 20px -8px rgba(43,26,15,0.14)",
              }}
            >
              <TapeCorner
                rotation={-3}
                style={{
                  top: -8,
                  left: 22,
                  background: "rgba(168,122,58,0.35)",
                }}
              />
              <p
                style={{
                  fontFamily: "var(--f-display)",
                  fontStyle: "italic",
                  fontSize: 18,
                  lineHeight: 1.5,
                  color: "var(--ink)",
                  margin: "0 0 10px",
                }}
              >
                „{s.quote.text}"
              </p>
              <div
                style={{
                  fontFamily: "var(--f-script)",
                  fontSize: 20,
                  color: "var(--oxblood)",
                  textAlign: "right",
                }}
              >
                — {s.quote.who}
              </div>
            </div>
          )}
        </section>
      ))}
      <Fleuron style={{ margin: "16px 0 4px", color: "var(--ochre)" }} />
    </article>
  );
}
